/**
 * A finished race played again in the browser, from its published chart (GET /races/:id/chart-data),
 * on the live race page: at replay time t (race seconds) the page gets the chart as it stood then.
 *
 * Each lap's end on the race clock: the crossing's time of day (LapData.hr) less the race start, when
 * the timing gives it (IMSA, SRO); otherwise the car's lap times summed (WRL charts carry no crossing
 * times, so a red-flag hold is left out of the clock; the time to go runs on the same clock, so the
 * laps to go stay consistent). The replay runs until the last car takes the flag: the time to go
 * runs out at the scheduled length (the winner's finish down to 5 minutes), and the checkered flag
 * comes when the leader finishes the lap it was on.
 *
 * At time t the chart keeps each car's laps ended by t; its pit markers once the out-lap is done (a
 * stop's times are only known then); position-change notes up to its last lap; the cautions and race
 * control's pit-lane calls up to the leader's lap. Nothing from later in the race: finishing places
 * become the places at t (most laps, then the earlier crossing), and the analysis package's stints
 * and drive times are left out.
 */
import type { AnnotationData, CarStint, PitMarker, RaceChartData } from "@shared/types";

/** The replay's race clock. */
export interface ReplayTimeline {
  /** Each car's lap ends (race s), by chart key, in lap order. */
  ends: Record<string, number[]>;
  /** Every lap end in the race, sorted: the cut changes only when t passes one. */
  all: number[];
  /** The replay's length (s): until the last car takes the flag. */
  lengthS: number;
  /** The checkered flag (s): the winner's last crossing, when the leader finishes its last lap. */
  finishS: number;
  /** The race's scheduled length (s): the finish down to 5 minutes (the time to go runs out here). */
  scheduledS: number;
  /** The clock comes from crossing times (not summed lap times). */
  fromCrossings: boolean;
}

/** Share of laps that must carry a crossing time for the clock to use them. */
const CROSSINGS_SHARE = 0.9;

export function replayTimeline(data: RaceChartData): ReplayTimeline {
  const cars = Object.entries(data.cars);
  const laps = cars.reduce((n, [, c]) => n + c.laps.length, 0);
  const withHr = cars.reduce((n, [, c]) => n + c.laps.filter((l) => typeof l.hr === "number").length, 0);
  const fromCrossings = laps > 0 && withHr / laps >= CROSSINGS_SHARE;
  const ends: Record<string, number[]> = {};
  if (fromCrossings) {
    // The race start: the earliest first crossing less its lap time (the green, for the car at the front).
    let start = Infinity;
    for (const [, c] of cars) {
      const l = c.laps[0];
      if (l && typeof l.hr === "number" && l.ltSec > 0) start = Math.min(start, l.hr - l.ltSec);
    }
    for (const [k, c] of cars) {
      const out: number[] = [];
      let prev = -Infinity;
      for (const l of c.laps) {
        let x = typeof l.hr === "number" ? l.hr - start : prev + (l.ltSec > 0 ? l.ltSec : 0);
        // Past midnight the time of day starts again.
        while (x < prev - 43200) x += 86400;
        out.push(x);
        prev = x;
      }
      ends[k] = out;
    }
  } else {
    for (const [k, c] of cars) {
      let sum = 0;
      ends[k] = c.laps.map((l) => (sum += l.ltSec > 0 ? l.ltSec : 0));
    }
  }
  const all = Object.values(ends).flat().sort((a, b) => a - b);
  const last = all.length ? all[all.length - 1] : 0;
  // The winner's last crossing: the earliest end of the most laps run (after the time runs out the
  // leader still has to finish its lap; in a race over a set number of laps, its last lap).
  const most = Math.max(0, ...Object.values(ends).map((e) => e.length));
  const finals = Object.values(ends).filter((e) => e.length === most && most > 0).map((e) => e[most - 1]);
  const finishS = finals.length ? Math.min(...finals) : last;
  return { ends, all, lengthS: Math.max(1, last), finishS, scheduledS: Math.max(300, Math.floor(finishS / 300) * 300), fromCrossings };
}

/** Lap ends at or before t (binary search in the sorted list): the cut changes only when this does. */
export function lapEndsBy(tl: ReplayTimeline, t: number): number {
  let lo = 0;
  let hi = tl.all.length;
  while (lo < hi) {
    const m = (lo + hi) >> 1;
    if (tl.all[m] <= t) lo = m + 1;
    else hi = m;
  }
  return lo;
}

export interface ReplayCut {
  data: RaceChartData;
  annotations: AnnotationData;
  /** The overall leader's laps done. */
  leaderLap: number;
  /** Under a caution at t, from the caution bands (the leader running a lap in one). */
  caution: boolean;
}

/** The chart as it stood at race time t. */
export function replayCut(data: RaceChartData, annotations: AnnotationData | null, tl: ReplayTimeline, t: number): ReplayCut {
  const cars: RaceChartData["cars"] = {};
  const done: Record<string, number> = {};
  for (const [k, c] of Object.entries(data.cars)) {
    const e = tl.ends[k] ?? [];
    let n = 0;
    while (n < e.length && e[n] <= t) n++;
    done[k] = n;
    const laps = c.laps.slice(0, n);
    const lastLap = laps[laps.length - 1];
    // Places from the last lap run; nothing from after t.
    const rest = { ...c };
    delete rest.driveTimes;
    delete rest.stints;
    delete rest.status;
    cars[k] = { ...rest, laps, finishPos: lastLap?.p ?? 0, finishPosClass: lastLap?.cp ?? 0 };
  }
  // Places now, as the timing gives them live: most laps done, then the earlier crossing of the
  // line (a car in the garage drops back as the field passes it), overall and in class.
  // Before a car's first crossing: in the order the field crosses the line on lap 1 (the grid).
  const at = (k: string) => (done[k] > 0 ? tl.ends[k][done[k] - 1] : (tl.ends[k]?.[0] ?? Infinity));
  const order = Object.keys(cars)
    .filter((k) => (tl.ends[k]?.length ?? 0) > 0)
    .sort((a, b) => done[b] - done[a] || at(a) - at(b));
  const inClass = new Map<string, number>();
  order.forEach((k, i) => {
    const cls = cars[k].cls;
    const p = (inClass.get(cls) ?? 0) + 1;
    inClass.set(cls, p);
    cars[k].finishPos = i + 1;
    cars[k].finishPosClass = p;
  });
  // The overall leader: most laps, then the earlier crossing.
  let leaderLap = 0;
  for (const n of Object.values(done)) leaderLap = Math.max(leaderLap, n);
  // Caution bands (the leader's laps under a caution) up to its last lap done; under a caution while
  // that lap is in one, as the timing shows it (a lap is flagged when it ends).
  const fcy: [number, number][] = [];
  for (const [a, b] of data.fcy ?? []) if (a <= leaderLap) fcy.push([a, Math.min(b, leaderLap)]);
  // The leader runs laps a..b of a band under the caution (lap b is flagged as the caution ends on
  // it): from its end of lap a - 1 to its end of lap b. Used only when the race has no flag times.
  const caution = (data.fcy ?? []).some(([a, b]) => a - 1 <= leaderLap && leaderLap < b);
  const out: RaceChartData = {
    ...data,
    cars,
    maxLap: leaderLap,
    fcy,
    ...(data.pitLane ? { pitLane: data.pitLane.filter((p) => p.lap <= leaderLap) } : {}),
  };
  const ann: AnnotationData = {};
  for (const [k, a] of Object.entries(annotations ?? {})) {
    const n = done[k] ?? 0;
    ann[k] = {
      ...a,
      // A stop shows once its out-lap is done (its time lost is known then).
      pits: a.pits.filter((p) => p.l + 1 <= n),
      // Who is driving now: the driver the next stop takes out of the car (the timing names the
      // driver in the car as it runs), else the race's stint that holds this lap.
      current: currentDriver(a.pits, data.cars[k]?.stints, n),
      settles: a.settles.filter((x) => x.l <= n),
      reasons: Object.fromEntries(Object.entries(a.reasons).filter(([l]) => Number(l) <= n)),
    };
  }
  return { data: out, annotations: ann, leaderLap, caution };
}

/** The driver of the stint under way after `n` laps: the next stop's driver in, else the stint holding lap n + 1. */
export function currentDriver(pits: PitMarker[], stints: CarStint[] | undefined, n: number): string | null {
  const next = pits.filter((p) => p.l + 1 > n).sort((a, b) => a.l - b.l)[0];
  if (next?.inDriver?.trim()) return next.inDriver.trim();
  const lap = Math.max(1, n);
  const st = (stints ?? []).find((x) => x.startLap <= lap && lap <= x.endLap);
  return st?.driver?.trim() || null;
}

/** "1:05:09" for 3909 s (the live status's clock strings). */
export function clockText(s: number): string {
  const x = Math.max(0, Math.floor(s));
  return `${Math.floor(x / 3600)}:${String(Math.floor((x % 3600) / 60)).padStart(2, "0")}:${String(x % 60).padStart(2, "0")}`;
}

/** The replay speeds the player offers. */
export const REPLAY_SPEEDS = [1, 2, 3] as const;
export type ReplaySpeed = (typeof REPLAY_SPEEDS)[number];
/** Rewind and skip steps (race s). */
export const REPLAY_STEP_S = 60;

/** A replay's race clock: race time `atS` at this device's clock `wallMs`, running at `speed` while `playing`. */
export interface ReplayClock {
  atS: number;
  wallMs: number;
  speed: ReplaySpeed;
  playing: boolean;
}

/** The race time on the replay's clock at this device's time `nowMs`, held within the race. */
export function replayTime(c: ReplayClock, nowMs: number, lengthS: number): number {
  const t = c.playing ? c.atS + ((nowMs - c.wallMs) / 1000) * c.speed : c.atS;
  return Math.min(lengthS, Math.max(0, t));
}

/** The clock moved to race time t (paused or playing as it was). */
export const seekClock = (c: ReplayClock, t: number, nowMs: number, lengthS: number): ReplayClock => ({ ...c, atS: Math.min(lengthS, Math.max(0, t)), wallMs: nowMs });
